import React, { useEffect, useRef, useState } from 'react';
import {
  Container, Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Alert,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { Search } from 'react-feather';

import Breadcrumbs from '../../Common/BreadCrumb/BreadCrumbs.component';
import SpinnerButton from '../../Common/SpinnerButton/SpinnerButton.component';

import ManualEntryTable from './components/ManualEntryTable.component';
import CsvUpload from './components/CsvUpload.component';
import ResultsView from './components/ResultsView.component';
import { EMPTY_ROW, DEFAULT_LIMIT } from './searchScrape.constants';
import { buildQuery } from './searchScrape.queryBuilder';
import { runSync, runBatch, fetchBatchStatus } from './searchScrape.api';

const POLL_INTERVAL = 2500;

export default function SearchScrape() {
  const [activeTab, setActiveTab] = useState('manual');
  const [manualRows, setManualRows] = useState([{ ...EMPTY_ROW }]);
  const [csvRows, setCsvRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const validRows = (rows) => rows.filter((r) => buildQuery(r));

  const resetOutput = () => {
    clearInterval(pollRef.current);
    setResults([]);
    setProgress(null);
  };

  // ---- Manual (synchronous) ----
  const handleManualRun = async (e) => {
    e.preventDefault();
    const rows = validRows(manualRows);
    if (!rows.length) {
      toast.error('Add at least one row with a search string.');
      return;
    }
    resetOutput();
    setLoading(true);
    try {
      const data = await runSync(rows, {});
      setResults(data.results || []);
      toast.success('Run complete');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Run failed');
    } finally {
      setLoading(false);
    }
  };

  // ---- CSV (async batch + polling) ----
  const pollBatch = (batchId) => {
    pollRef.current = setInterval(async () => {
      try {
        const data = await fetchBatchStatus(batchId);
        const batch = data.batch || {};
        setResults(batch.results || []);
        setProgress({ status: batch.status, completed: batch.completed || 0, total: batch.total || 0 });
        if (batch.status === 'finished' || batch.status === 'failed') {
          clearInterval(pollRef.current);
          setLoading(false);
          if (batch.status === 'finished') toast.success('Batch complete');
          else toast.error(`Batch failed: ${batch.error || 'unknown error'}`);
        }
      } catch (err) {
        clearInterval(pollRef.current);
        setLoading(false);
        toast.error(err.response?.data?.message || err.message || 'Lost track of batch');
      }
    }, POLL_INTERVAL);
  };

  const handleBatchRun = async () => {
    const rows = validRows(csvRows);
    if (!rows.length) {
      toast.error('Upload a CSV with at least one valid row.');
      return;
    }
    resetOutput();
    setLoading(true);
    try {
      const data = await runBatch(rows, {});
      setProgress({ status: 'queued', completed: 0, total: data.total });
      pollBatch(data.batch_id);
    } catch (err) {
      setLoading(false);
      toast.error(err.response?.data?.message || err.message || 'Failed to start batch');
    }
  };

  return (
    <React.Fragment>
      <Container fluid>
        <Breadcrumbs childrenObj={[{ name: 'Internal Dashboard', url: '/internal' }]} />
        <div className="mb-3">
          <h3 className="mb-1"><Search size={22} className="me-2" />SERP Search &amp; Scrape</h3>
          <p className="text-muted mb-0">
            Enter a <b>Search</b> string and <b>Limit</b> (1&ndash;{DEFAULT_LIMIT}) &mdash; we query Google via
            <b> Serpix</b>, take the top URLs, and scrape each with <b>Scrapix</b>. Search supports Google operators
            (e.g. <code>site:amazon.in toys</code>).
          </p>
        </div>

        <Card className="mb-3">
          <CardBody>
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink href="#" active={activeTab === 'manual'} onClick={(e) => { e.preventDefault(); setActiveTab('manual'); }}>
                  Manual Entry
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink href="#" active={activeTab === 'csv'} onClick={(e) => { e.preventDefault(); setActiveTab('csv'); }}>
                  CSV Upload
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="manual">
                <Alert color="light" className="border py-2 small">
                  Manual runs execute immediately (up to 5 rows). Leave <b>Limit</b> blank to scrape the top {DEFAULT_LIMIT}.
                </Alert>
                <ManualEntryTable rows={manualRows} onChange={setManualRows} disabled={loading} />
                <div className="mt-3">
                  <SpinnerButton color="primary" loading={loading} onClick={handleManualRun}>
                    Run Search &amp; Scrape
                  </SpinnerButton>
                </div>
              </TabPane>

              <TabPane tabId="csv">
                <CsvUpload onParsed={setCsvRows} disabled={loading} />
                <div className="mt-3">
                  <SpinnerButton color="primary" loading={loading} disabled={loading || csvRows.length === 0} onClick={handleBatchRun}>
                    Run Batch ({csvRows.length} rows)
                  </SpinnerButton>
                </div>
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>

        <ResultsView results={results} progress={progress} />
      </Container>
    </React.Fragment>
  );
}
